import * as echarts from 'echarts'

// Ecomac Brand Color Palette based on CSS variables
export const ecomacTheme = {
  color: [
    '#0a2a3f', // Primary color
    '#4B5563', // Secondary color
    '#28A745', // Success color
    '#3182CE', // Info color
    '#F59E0B', // Warning color
    '#DC2626', // Error/Danger color
    '#6B7280', // Secondary light
    '#004AAD', // Link color
    '#1E7E34', // Success dark
    '#2C5282', // Info dark
    '#D97706', // Warning dark
    '#B91C1C'  // Error dark
  ],
  backgroundColor: '#FFFFFF',
  textStyle: {
    fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
    fontSize: 12,
    color: '#1F2937'
  },
  title: {
    textStyle: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 18,
      fontWeight: 600,
      color: '#1F2937'
    },
    subtextStyle: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 14,
      color: '#4B5563'
    }
  },
  legend: {
    textStyle: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 12,
      color: '#1F2937'
    }
  },
  categoryAxis: {
    axisLine: {
      lineStyle: {
        color: '#E5E7EB'
      }
    },
    axisTick: {
      lineStyle: {
        color: '#E5E7EB'
      }
    },
    axisLabel: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 11,
      color: '#6B7280'
    },
    splitLine: {
      lineStyle: {
        color: '#F3F4F6'
      }
    }
  },
  valueAxis: {
    axisLine: {
      lineStyle: {
        color: '#E5E7EB'
      }
    },
    axisTick: {
      lineStyle: {
        color: '#E5E7EB'
      }
    },
    axisLabel: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 11,
      color: '#6B7280'
    },
    splitLine: {
      lineStyle: {
        color: '#F3F4F6'
      }
    }
  },
  grid: {
    borderColor: '#E5E7EB'
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderColor: '#E5E7EB',
    borderWidth: 1,
    textStyle: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 12,
      color: '#1F2937'
    },
    extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); border-radius: 6px;'
  }
}

// Register the Ecomac theme
echarts.registerTheme('ecomac', ecomacTheme)

// Default chart options with Ecomac branding
export const defaultChartOptions = {
  backgroundColor: '#FFFFFF',
  textStyle: {
    fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif'
  },
  animation: true,
  animationDuration: 1000,
  animationEasing: 'cubicOut',
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  tooltip: {
    trigger: 'axis',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderColor: '#E5E7EB',
    borderWidth: 1,
    textStyle: {
      fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
      fontSize: 12,
      color: '#1F2937'
    },
    extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); border-radius: 6px;'
  }
}

// Utility function to merge user options with default options
export const mergeChartOptions = (userOptions: any = {}) => {
  return {
    ...defaultChartOptions,
    ...userOptions,
    textStyle: {
      ...defaultChartOptions.textStyle,
      ...userOptions.textStyle
    },
    tooltip: {
      ...defaultChartOptions.tooltip,
      ...userOptions.tooltip,
      textStyle: {
        ...defaultChartOptions.tooltip.textStyle,
        ...userOptions.tooltip?.textStyle
      }
    }
  }
}

// Preset chart configurations for common chart types
export const chartPresets = {
  // Line chart preset with Ecomac styling
  line: (data: any, options: any = {}) => mergeChartOptions({
    xAxis: {
      type: 'category',
      data: data.categories || [],
      axisLabel: {
        fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
        fontSize: 11,
        color: '#6B7280'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
        fontSize: 11,
        color: '#6B7280'
      }
    },
    series: [{
      type: 'line',
      data: data.values || [],
      smooth: true,
      lineStyle: {
        width: 3
      },
      itemStyle: {
        borderRadius: 4
      }
    }],
    ...options
  }),

  // Bar chart preset with Ecomac styling
  bar: (data: any, options: any = {}) => mergeChartOptions({
    xAxis: {
      type: 'category',
      data: data.categories || [],
      axisLabel: {
        fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
        fontSize: 11,
        color: '#6B7280'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif',
        fontSize: 11,
        color: '#6B7280'
      }
    },
    series: [{
      type: 'bar',
      data: data.values || [],
      itemStyle: {
        borderRadius: [4, 4, 0, 0]
      }
    }],
    ...options
  }),

  // Pie chart preset with Ecomac styling
  pie: (data: any, options: any = {}) => mergeChartOptions({
    series: [{
      type: 'pie',
      data: data.items || [],
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 4,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'center',
        fontFamily: 'Poppins, Roboto, ui-sans-serif, system-ui, sans-serif'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 20,
          fontWeight: 600
        }
      },
      labelLine: {
        show: false
      }
    }],
    ...options
  }),

  // Donut chart preset (similar to pie but with center text capability)
  donut: (data: any, options: any = {}) => mergeChartOptions({
    series: [{
      type: 'pie',
      data: data.items || [],
      radius: ['50%', '80%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 6,
        borderColor: '#fff',
        borderWidth: 3
      },
      label: {
        show: false
      },
      labelLine: {
        show: false
      }
    }],
    ...options
  })
}

// Export echarts for direct usage if needed
export { echarts }

// Plugin installation function for Vue 3
export default {
  install(app: any) {
    app.config.globalProperties.$echarts = echarts
    app.config.globalProperties.$ecomacTheme = ecomacTheme
    app.config.globalProperties.$chartPresets = chartPresets
    app.provide('echarts', echarts)
    app.provide('ecomacTheme', ecomacTheme)
    app.provide('chartPresets', chartPresets)
  }
}
